// =============================================================================
// SiteFolderSetup — the manager's Site folder tab on a project (mig 393)
// =============================================================================
// James, 27 Sep 2026: "when a manager saves as Ops they absolutely 100% have
// to set it up. You have to allocate the SWMS and sign off as a manager, the
// permits required ... a minimum will need to be set."
//
// Six steps down the left, the current one on the right. Client-run jobs
// only need steps 1 and 5 (the folder is live regardless — hospital, map,
// contacts, report). Coretel-run jobs need all six, and the crew's folder
// says "being set up by the office" until step 6 is signed. Any change to
// scope / SWMS / permits after sign-off drops the sign-off (trigger, mig 393).
// =============================================================================

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, X, AlertTriangle, ShieldCheck, FileCheck2, Siren, PenLine, FolderOpen, ExternalLink, Phone, Plus, MapPin, Flag, Users, Map as MapIcon, Info,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Input, Select, Textarea, Pill, Spinner } from "@/components/ui";
import {
  useSiteFolder, setupSteps, requiredPermitsFor, requiredDocsFor, isHrcw, uncoveredActivities,
  PERMIT_LABEL, PERMIT_TYPES, CONTACT_CATEGORY_LABEL, allocKey, type HseSetup, type SwmsAllocation, type SwmsDoc, type StateContact,
} from "./siteFolderData";
import { MedicalLookup, medicalFromSite, medicalToPayload, type MedicalValues } from "@/features/sites/MedicalLookup";

const PLATFORM_HINTS = ["Novus", "SafetyCulture", "SiteDocs"];

export default function SiteFolderSetup({ projectId }: { projectId: string }) {
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");
  const f = useSiteFolder(projectId);
  const [step, setStep] = useState<number | null>(null);

  const steps = useMemo(() => f.project && f.setup
    ? setupSteps({ coretel: f.coretel, setup: f.setup, rules: f.rules, site: f.site, contacts: f.contacts, siteMap: f.siteMap, stateContacts: f.stateContacts })
    : [], [f.project, f.setup, f.rules, f.site, f.contacts, f.siteMap, f.stateContacts, f.coretel]);
  const firstOpen = steps.find(s => !s.done && (f.coretel || s.key === "emergency"))?.n ?? 1;
  const current = step ?? firstOpen;
  const doneCount = steps.filter(s => s.done && (f.coretel || ["system", "emergency"].includes(s.key))).length;
  const needCount = f.coretel ? 6 : 2;
  const ready = f.coretel ? !!f.setup?.signed_off_at : steps.find(s => s.key === "emergency")?.done ?? false;

  if (f.isLoading || !f.project || !f.setup) return <div className="py-12 flex justify-center"><Spinner size={20}/></div>;

  return (
    <div className="space-y-4">
      {/* status strip */}
      <div className={`rounded-xl border px-5 py-3 flex items-center gap-4 flex-wrap ${
        ready ? "border-ok/40 bg-ok/8" : f.coretel ? "border-warn/50 bg-warn/10" : "border-info/40 bg-info/8"}`}>
        <span className={`w-9 h-9 rounded-full text-white flex items-center justify-center font-bold shrink-0 ${ready ? "bg-ok" : f.coretel ? "bg-warn" : "bg-info"}`}>
          {ready ? <Check size={18}/> : f.coretel ? "!" : <Info size={18}/>}
        </span>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[14px] font-semibold text-text">
            {f.coretel
              ? (ready ? `Coretel paperwork — folder ready for the crew` : `Coretel paperwork — folder not set up yet. The crew sees "Being set up by the office".`)
              : `Paperwork in ${f.project.hse_client_platform || "the client's system"}${f.project.clients?.legal_name ? ` (${f.project.clients.legal_name})` : ""} — ${ready ? "folder live for the crew" : "folder live, but the emergency details need finishing"}`}
          </div>
          <div className="text-[12px] text-text-muted">
            {doneCount} of {needCount} steps done · {steps.filter(s => f.coretel || ["system", "emergency"].includes(s.key)).map(s => `${s.label.split(" —")[0]} ${s.done ? "✓" : "✗"}`).join(" · ")}
          </div>
        </div>
        <Link to={`/projects/${projectId}/site-folder`}><Button variant="secondary" size="sm" icon={FolderOpen}>Open as the crew sees it</Button></Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-start">
        {/* steps */}
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1">Set up the folder</div>
          {steps.map(s => {
            const skipped = !f.coretel && !["system", "emergency"].includes(s.key);
            const active = current === s.n;
            return (
              <button key={s.key} type="button" onClick={() => setStep(s.n)}
                className={`w-full text-left rounded-lg border px-3 py-2.5 flex items-center gap-3 transition-colors ${
                  active ? "border-accent bg-accent/8" : "border-line bg-ink-raised hover:bg-ink-hover"} ${skipped ? "opacity-50" : ""}`}>
                <span className={`w-6 h-6 rounded-full text-[12px] flex items-center justify-center shrink-0 ${
                  s.done ? "bg-ok text-white" : active ? "bg-accent text-white" : skipped ? "border-2 border-line text-text-muted" : "border-2 border-danger text-danger"}`}>
                  {s.done ? <Check size={13}/> : skipped ? s.n : s.done === false && !active ? <X size={12}/> : s.n}
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-text">{s.n} · {s.label}</div>
                  <div className={`text-[11px] truncate ${s.done || skipped ? "text-text-muted" : "text-danger"}`}>{skipped ? "Not needed for client paperwork" : s.detail}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* body */}
        <div className="rounded-xl border border-line bg-ink-raised p-5 md:p-6">
          {current === 1 && <StepSystem f={f} canEdit={canEdit} onNext={() => setStep(f.coretel ? 2 : 5)}/>}
          {current === 2 && <StepScope f={f} canEdit={canEdit} onNext={() => setStep(3)}/>}
          {current === 3 && <StepSwms f={f} canEdit={canEdit} onNext={() => setStep(4)}/>}
          {current === 4 && <StepPermits f={f} canEdit={canEdit} onNext={() => setStep(5)}/>}
          {current === 5 && <StepEmergency f={f} canEdit={canEdit} user={user} onNext={() => setStep(f.coretel ? 6 : 5)}/>}
          {current === 6 && <StepSignoff f={f} steps={steps} canEdit={canEdit} user={user}/>}
        </div>
      </div>
    </div>
  );
}

type F = ReturnType<typeof useSiteFolder>;

async function saveSetup(f: F, patch: Partial<HseSetup>, userId?: string | null) {
  const row = { ...(f.setup as HseSetup), ...patch, project_id: f.project.project_id, updated_by: userId ?? null };
  delete (row as any).updated_at;
  const { error } = await (supabase as any).from("project_hse_setup").upsert(row, { onConflict: "project_id" });
  if (error) throw error;
  f.invalidateAll();
}

function StepHead({ n, title, blurb }: { n: number; title: string; blurb: string }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-wider text-text-muted">Step {n}</div>
      <h2 className="text-[18px] font-semibold text-text mt-0.5">{title}</h2>
      <p className="text-[13px] text-text-muted mt-1 max-w-[680px]">{blurb}</p>
    </div>
  );
}
function StepFoot({ err, busy, onSave, saveLabel, disabled }: { err: string | null; busy: boolean; onSave: () => void; saveLabel: string; disabled?: boolean }) {
  return (
    <div className="mt-5 flex items-center justify-end gap-3">
      {err && <span className="text-[12px] text-danger mr-auto">{err}</span>}
      <Button onClick={onSave} disabled={busy || disabled}>{busy ? "Saving…" : saveLabel}</Button>
    </div>
  );
}

// --- 1 · whose paperwork ------------------------------------------------------
function StepSystem({ f, canEdit, onNext }: { f: F; canEdit: boolean; onNext: () => void }) {
  const [system, setSystem] = useState<string>(f.project.hse_system ?? "client");
  const [platform, setPlatform] = useState<string>(f.project.hse_client_platform ?? "");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  async function save() {
    setBusy(true); setErr(null);
    const { error } = await (supabase as any).from("projects")
      .update({ hse_system: system, hse_client_platform: system === "client" ? (platform.trim() || null) : null }).eq("project_id", f.project.project_id);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    f.invalidateAll(); onNext();
  }
  return (
    <div>
      <StepHead n={1} title="Whose paperwork runs this job?" blurb="Every job starts on the client's system. Switch it to Coretel only when our start card, SWMS and permits run the job — then the folder has to be set up before the crew can use it."/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { v: "client", t: "Client's system", d: "Ventia → Novus · APD → SafetyCulture · HVAC → SiteDocs. The crew does the paperwork there; the folder gives them the site, the map and emergencies." },
          { v: "coretel", t: "Coretel — done in Ops", d: "Our start card, SWMS and permits. Six-step setup and a manager sign-off before the crew sees it." },
        ].map(o => (
          <button key={o.v} type="button" disabled={!canEdit} onClick={() => setSystem(o.v)}
            className={`text-left rounded-lg border-2 p-4 transition-colors ${system === o.v ? "border-accent bg-accent/5" : "border-line hover:bg-ink-hover"}`}>
            <div className="flex items-center gap-2"><span className={`w-5 h-5 rounded flex items-center justify-center ${system === o.v ? "bg-accent text-white" : "border border-line-light"}`}>{system === o.v && <Check size={12}/>}</span><span className="font-semibold text-[14px] text-text">{o.t}</span></div>
            <div className="text-[12px] text-text-muted mt-2">{o.d}</div>
          </button>
        ))}
      </div>
      {system === "client" && (
        <div className="mt-4 max-w-[360px]">
          <div className="text-[11px] text-text-muted mb-1">Their platform (the crew sees this)</div>
          <Input list="hse-platform-hints" value={platform} onChange={e => setPlatform(e.target.value)} placeholder="Novus / SafetyCulture / SiteDocs" disabled={!canEdit}/>
          <datalist id="hse-platform-hints">{PLATFORM_HINTS.map(h => <option key={h} value={h}/>)}</datalist>
        </div>
      )}
      {canEdit && <StepFoot err={err} busy={busy} onSave={save} saveLabel={system === "coretel" ? "Save → Scope of works" : "Save → Emergency & site"}/>}
    </div>
  );
}

// --- 2 · scope --------------------------------------------------------------
function StepScope({ f, canEdit, onNext }: { f: F; canEdit: boolean; onNext: () => void }) {
  const user = useRequireAuth();
  const [keys, setKeys] = useState<string[]>(f.setup!.scope_keys ?? []);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const permits = requiredPermitsFor(keys, f.rules); const docs = requiredDocsFor(keys, f.rules); const hrcw = isHrcw(keys, f.rules);
  function toggle(k: string) { setKeys(ks => ks.includes(k) ? ks.filter(x => x !== k) : [...ks, k]); }
  async function save() {
    if (keys.length === 0) { setErr("Tick at least one activity."); return; }
    setBusy(true); setErr(null);
    try {
      // the minimum permit set follows the scope; keep any extras the manager added
      const extras = (f.setup!.required_permits ?? []).filter(p => !requiredPermitsFor(f.setup!.scope_keys, f.rules).includes(p));
      await saveSetup(f, { scope_keys: keys, required_permits: PERMIT_TYPES.filter(p => permits.includes(p) || extras.includes(p)) }, user?.user_id);
      if (hrcw !== !!f.project.is_high_risk_construction_work) {
        await (supabase as any).from("projects").update({ is_high_risk_construction_work: hrcw }).eq("project_id", f.project.project_id);
      }
      onNext();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <div>
      <StepHead n={2} title="What's in scope on this job?" blurb="Tick the activities. Each one fires the rules underneath it — the SWMS and permits become the minimum set the crew must have before work starts."/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {f.rules.map(r => {
          const on = keys.includes(r.activity_key);
          return (
            <button key={r.activity_key} type="button" disabled={!canEdit} onClick={() => toggle(r.activity_key)}
              className={`text-left rounded-lg border-2 p-4 transition-colors ${on ? "border-accent bg-accent/5" : "border-line hover:bg-ink-hover"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${on ? "bg-accent text-white" : "border border-line-light"}`}>{on && <Check size={12}/>}</span>
                <span className="font-semibold text-[14px] text-text">{r.label}</span>
                {r.is_hrcw && <span className="ml-auto text-[10px] font-bold text-danger">HRCW</span>}
              </div>
              {r.hint && <div className="text-[11px] text-text-muted mt-1 ml-7">{r.hint}</div>}
              <div className="mt-2 ml-7 text-[12px] text-text-muted space-y-0.5">
                {(r.requires?.permits ?? []).map(p => <div key={p}>→ <b className="text-text">{PERMIT_LABEL[p] ?? p} permit</b></div>)}
                {(r.requires?.docs ?? []).map(d => <div key={d}>→ <b className="text-text">{d}</b></div>)}
                {(r.requires?.notes ?? []).map(n => <div key={n}>→ {n}</div>)}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-4 rounded-lg bg-ink px-4 py-3 text-[13px] text-text">
        <b>Minimum set for this job:</b>{" "}
        {keys.length === 0 ? <span className="text-text-muted">nothing ticked yet</span> : <>
          SWMS covering {keys.map(k => f.rules.find(r => r.activity_key === k)?.label.toLowerCase()).join(" + ")}
          {permits.length > 0 && <> · {permits.map(p => `${PERMIT_LABEL[p]} permit`).join(" · ")}</>}
          {docs.length > 0 && <> · {docs.join(" · ")}</>}
          <span className="text-text-muted"> — HRCW: {hrcw ? "yes" : "no"}</span>
        </>}
      </div>
      {canEdit && <StepFoot err={err} busy={busy} onSave={save} saveLabel="Save scope → SWMS"/>}
    </div>
  );
}

// --- 3 · SWMS ---------------------------------------------------------------
function StepSwms({ f, canEdit, onNext }: { f: F; canEdit: boolean; onNext: () => void }) {
  const user = useRequireAuth();
  const [alloc, setAlloc] = useState<SwmsAllocation[]>(f.setup!.swms ?? []);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const scope = f.setup!.scope_keys ?? [];
  const uncovered = uncoveredActivities({ ...f.setup!, swms: alloc });
  const siteDocs = f.swmsList.filter(d => d.kind === "site");
  const libDocs = f.swmsList.filter(d => d.kind === "library");
  function isOn(key: string) { return alloc.some(a => allocKey(a) === key); }
  // Ticking a SWMS covers everything in scope by default — the manager unticks
  // what it doesn't cover. (First cut started empty and read as an error.)
  function toggleSwms(d: SwmsDoc) {
    setAlloc(a => isOn(d.key) ? a.filter(x => allocKey(x) !== d.key)
      : [...a, d.kind === "site" ? { project_swms_id: d.id, swms_id: null, covers: [...scope] } : { project_swms_id: null, swms_id: d.id, covers: [...scope] }]);
  }
  function toggleCover(key: string, k: string) {
    setAlloc(a => a.map(x => allocKey(x) !== key ? x : { ...x, covers: x.covers.includes(k) ? x.covers.filter(c => c !== k) : [...x.covers, k] }));
  }
  const renderDoc = (d: SwmsDoc) => {
    const on = isOn(d.key); const a = alloc.find(x => allocKey(x) === d.key);
    return (
      <div key={d.key} className={`rounded-lg border-2 p-3 ${on ? "border-accent bg-accent/5" : "border-line"}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" disabled={!canEdit} onClick={() => toggleSwms(d)} className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${on ? "bg-accent text-white" : "border border-line-light"}`}>{on && <Check size={12}/>}</button>
          <span className="font-mono text-[12px] text-accent">{d.number}</span>
          <span className="text-[13px] text-text flex-1 min-w-[160px]">{d.title}</span>
          {d.kind === "library"
            ? (d.status === "approved" ? <Pill label={`standard · v${d.version}`} tone="ok" small/> : d.status === "draft" ? <Pill label="template draft" tone="warning" small/> : <Pill label="standard template" tone="notice" small/>)
            : (d.status === "approved" ? <Pill label={`site · v${d.version} approved`} tone="ok" small/> : <Pill label={`site · ${d.status} — director to approve`} tone="warning" small/>)}
          <Link to={d.href} className="text-[12px] text-accent hover:underline inline-flex items-center gap-1"><ExternalLink size={11}/>Open</Link>
        </div>
        {on && scope.length > 0 && (
          <div className="mt-2 ml-8 flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] text-text-muted mr-1">Covers (untick what it doesn't):</span>
            {scope.map(k => {
              const c = a?.covers.includes(k);
              return <button key={k} type="button" disabled={!canEdit} onClick={() => toggleCover(d.key, k)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border ${c ? "bg-ok/12 border-ok/50 text-ok" : "border-line-light text-text-muted hover:text-text line-through"}`}>
                <span className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center ${c ? "bg-ok text-white" : "border border-line-light"}`}>{c && <Check size={10}/>}</span>
                {f.rules.find(r => r.activity_key === k)?.label}
              </button>;
            })}
          </div>
        )}
      </div>
    );
  };
  async function save() {
    if (alloc.length === 0) { setErr("Allocate at least one site SWMS."); return; }
    if (uncovered.length) { setErr(`Not covered yet: ${uncovered.map(k => f.rules.find(r => r.activity_key === k)?.label).join(", ")}. Tick what each SWMS covers, or build one.`); return; }
    setBusy(true); setErr(null);
    try { await saveSetup(f, { swms: alloc }, user?.user_id); onNext(); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <div>
      <StepHead n={3} title="Allocate the SWMS" blurb="Standard templates from the library, site-specific SWMS built for this job, or both. Tick which in-scope activities each one covers — every activity must be covered. A site SWMS still in draft shows here but won't pass sign-off until a director approves it."/>
      {scope.length === 0 && <div className="text-[12px] text-warn mb-3 flex items-center gap-1.5"><AlertTriangle size={13}/> Do step 2 first — nothing is in scope yet.</div>}
      <div className="space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Site-specific SWMS for this job</div>
          {siteDocs.length === 0
            ? <div className="rounded-lg border border-dashed border-line p-3 text-[12px] text-text-muted">None built for this job. <Link to="/hse/swms" className="text-accent hover:underline">Build one in HSE → SWMS</Link> when the standard template isn't enough — the AI merges the base with this site's conditions and a director approves it.</div>
            : <div className="space-y-2">{siteDocs.map(renderDoc)}</div>}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Standard templates (library)</div>
          {libDocs.length === 0
            ? <div className="rounded-lg border border-dashed border-line p-3 text-[12px] text-text-muted">No templates in the library. A director adds them in HSE → SWMS → New template.</div>
            : <div className="space-y-2">{libDocs.map(renderDoc)}</div>}
        </div>
      </div>
      {scope.length > 0 && (
        <div className={`mt-4 rounded-lg px-4 py-3 text-[13px] ${uncovered.length ? "bg-danger/8 text-danger" : "bg-ok/8 text-ok"}`}>
          {uncovered.length ? <>Not covered: {uncovered.map(k => f.rules.find(r => r.activity_key === k)?.label).join(", ")}</> : <>Every in-scope activity is covered.</>}
        </div>
      )}
      {canEdit && <StepFoot err={err} busy={busy} onSave={save} saveLabel="Save SWMS → Permits"/>}
    </div>
  );
}

// --- 4 · permits ------------------------------------------------------------
function StepPermits({ f, canEdit, onNext }: { f: F; canEdit: boolean; onNext: () => void }) {
  const user = useRequireAuth();
  const auto = requiredPermitsFor(f.setup!.scope_keys ?? [], f.rules);
  const [extra, setExtra] = useState<string[]>((f.setup!.required_permits ?? []).filter(p => !auto.includes(p)));
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  async function save() {
    setBusy(true); setErr(null);
    try { await saveSetup(f, { required_permits: PERMIT_TYPES.filter(p => auto.includes(p) || extra.includes(p)) }, user?.user_id); onNext(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <div>
      <StepHead n={4} title="Permits the crew must have" blurb="The rules set the minimum from the scope — those are locked on. Add any others this job needs. The crew can still raise any permit on site; these are the ones the folder will chase them for."/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {PERMIT_TYPES.map(p => {
          const locked = auto.includes(p); const on = locked || extra.includes(p);
          return (
            <button key={p} type="button" disabled={!canEdit || locked} onClick={() => setExtra(x => x.includes(p) ? x.filter(y => y !== p) : [...x, p])}
              className={`text-left rounded-lg border-2 p-3 flex items-center gap-3 ${on ? "border-accent bg-accent/5" : "border-line hover:bg-ink-hover"} ${locked ? "cursor-default" : ""}`}>
              <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${on ? "bg-accent text-white" : "border border-line-light"}`}>{on && <Check size={12}/>}</span>
              <span className="text-[13px] font-medium text-text flex-1">{PERMIT_LABEL[p]}</span>
              {locked && <span className="text-[10px] uppercase tracking-wider text-accent">from scope</span>}
              {p === "working_at_heights" && on && <span className="text-[10px] text-text-muted">holder must be a rigger</span>}
            </button>
          );
        })}
      </div>
      {f.permits.length > 0 && (
        <div className="mt-4 text-[12px] text-text-muted">Already on this job: {f.permits.map((p: any) => `${p.permit_number} ${PERMIT_LABEL[p.permit_type] ?? p.permit_type} (${p.status})`).join(" · ")}</div>
      )}
      {canEdit && <StepFoot err={err} busy={busy} onSave={save} saveLabel="Save permits → Emergency & site"/>}
    </div>
  );
}

// --- 5 · emergency & site -----------------------------------------------------
function StepEmergency({ f, canEdit, user, onNext }: { f: F; canEdit: boolean; user: any; onNext: () => void }) {
  const s = f.site;
  // hospital + medical centre live in the shared block (features/sites/MedicalLookup) —
  // the same one the site library's New / Edit forms use, writing the same sites columns
  const [med, setMed] = useState<MedicalValues>(medicalFromSite(s));
  const [evac, setEvac] = useState(s?.evacuation_point ?? "");
  const [verified, setVerified] = useState<Record<string, boolean>>(Object.fromEntries(f.stateContacts.map(c => [c.contact_id, c.verified])));
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState({ category: "other", name: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setVerified(Object.fromEntries(f.stateContacts.map(c => [c.contact_id, c.verified]))); }, [f.stateContacts]);

  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState<Record<string, any>>({});
  const siteAddress = [s?.address_line1, s?.suburb, s?.state, s?.postcode].filter(Boolean).join(", ");
  async function checkNumbers() {
    setChecking(true); setErr(null);
    try {
      const r = await apiFetch("/hse/state-contacts/check", { method: "POST", body: JSON.stringify({ state: f.state ?? "AU",
        contacts: f.stateContacts.map(c => ({ contact_id: c.contact_id, name: c.name, phone: c.phone, category: c.category })) }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail ?? j.error ?? `check ${r.status}`);
      const map: Record<string, any> = {};
      for (const x of j.results ?? []) map[x.contact_id] = x;
      setChecks(map);
      // a confirmed match ticks itself; the manager still saves
      setVerified(v => { const n = { ...v }; for (const x of j.results ?? []) if (x.ok) n[x.contact_id] = true; return n; });
    } catch (e: any) { setErr(e.message); } finally { setChecking(false); }
  }
  async function applyPhone(c: StateContact, phone: string) {
    const { error } = await (supabase as any).from("hse_state_contacts").update({ phone, verified: true, verified_at: new Date().toISOString(), verified_by: user?.user_id ?? null }).eq("contact_id", c.contact_id);
    if (error) { setErr(error.message); return; }
    f.invalidateAll();
  }

  const assembly = f.siteMap?.features?.features?.find((x: any) => x.properties?.kind === "assembly");
  const fieldMgr = f.clientContacts.filter((c: any) => c.is_field_manager);

  async function save() {
    setBusy(true); setErr(null);
    try {
      if (s?.site_id) {
        const { error } = await (supabase as any).from("sites").update({
          ...medicalToPayload(med), evacuation_point: evac.trim() || null,
        }).eq("site_id", s.site_id);
        if (error) throw error;
      }
      const changed = f.stateContacts.filter(c => verified[c.contact_id] !== c.verified);
      for (const c of changed) {
        const { error } = await (supabase as any).from("hse_state_contacts")
          .update({ verified: !!verified[c.contact_id], verified_at: verified[c.contact_id] ? new Date().toISOString() : null, verified_by: verified[c.contact_id] ? user?.user_id ?? null : null })
          .eq("contact_id", c.contact_id);
        if (error) throw error;
      }
      const stateOk = f.stateContacts.filter(c => c.state !== "AU").every(c => verified[c.contact_id]);
      await saveSetup(f, {
        hospital_ok: !!med.nearest_hospital_name.trim(), evac_ok: !!(evac.trim() || assembly), contacts_ok: f.contacts.length > 0, map_ok: !!f.siteMap, state_contacts_ok: stateOk,
      }, user?.user_id);
      onNext();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  async function addContact() {
    if (!nc.name.trim() || !nc.phone.trim()) { setErr("Name and phone."); return; }
    const { error } = await (supabase as any).from("hse_state_contacts").insert({
      state: f.state ?? "AU", category: nc.category, name: nc.name.trim(), phone: nc.phone.trim(), note: nc.note.trim() || null,
      verified: true, verified_at: new Date().toISOString(), verified_by: user?.user_id ?? null, sort_order: 90,
    });
    if (error) { setErr(error.message); return; }
    setNc({ category: "other", name: "", phone: "", note: "" }); setAdding(false); f.invalidateAll();
  }

  const cats = ["emergency", "regulator", "wildlife", "power", "environment", "poisons", "ses", "dbyd", "other"];
  return (
    <div>
      <StepHead n={5} title="Emergency & site" blurb={`What the crew sees when it goes wrong. The hospital and assembly point save to the Site record (every future job here gets them). The state numbers are seeded from published sources but start unverified — tick each one once you've checked it${f.state ? ` for ${f.state}` : ""}.`}/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-line p-4">
          <MedicalLookup lat={s?.latitude} lng={s?.longitude} address={siteAddress} value={med} onChange={setMed} canEdit={canEdit}/>
        </div>
        <div className="rounded-lg border border-line p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5"><Flag size={12}/> Assembly / evacuation</div>
          <Textarea value={evac} onChange={e => setEvac(e.target.value)} rows={2} placeholder="e.g. Compound gate — the assembly flag on the map" disabled={!canEdit}/>
          <div className="text-[12px] text-text-muted flex items-center gap-1.5">
            <MapIcon size={12}/>
            {f.siteMap
              ? <>Site map {f.siteMap.map_number}{f.siteMap.is_locked ? ` locked v${f.siteMap.version}` : " — not locked"}{assembly ? " · assembly point marked" : " · no assembly pin yet"} · <Link to={`/hse/site-maps/${f.siteMap.site_map_id}`} className="text-accent hover:underline">open</Link></>
              : <>No site map yet · <Link to={`/hse/site-maps?project=${f.project.project_id}`} className="text-accent hover:underline">draw it</Link></>}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5 pt-2"><MapPin size={12}/> Site</div>
          <div className="text-[12px] text-text">{[s?.address_line1, s?.suburb, s?.state, s?.postcode].filter(Boolean).join(", ") || <span className="text-warn">No address on the site record</span>}</div>
          <div className="text-[12px] font-mono text-text-muted">{s?.latitude != null ? `${Number(s.latitude).toFixed(5)}, ${Number(s.longitude).toFixed(5)}` : <span className="text-warn font-sans">No GPS on the site record</span>}{s?.site_id && <> · <Link to={`/sites/${s.site_id}`} className="text-accent hover:underline font-sans">site record</Link></>}</div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-line p-4">
        <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5 mb-2"><Users size={12}/> Who the crew calls</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px]">
          <div>
            <div className="text-text-muted mb-1">Coretel — from the project team</div>
            {f.contacts.length === 0 ? <div className="text-warn">Nobody assigned — <Link to={`/projects/${f.project.project_id}?tab=overview`} className="text-accent hover:underline">assign a PM / site lead on Overview</Link></div>
              : f.contacts.map((c, i) => <div key={i} className="text-text">{c.full_name} <span className="text-text-muted capitalize">· {String(c.contact_role).replace(/_/g, " ")}{c.mobile ? ` · ${c.mobile}` : " · no mobile"}</span></div>)}
          </div>
          <div>
            <div className="text-text-muted mb-1">{f.project.clients?.legal_name ?? "Client"} — field manager for {f.state ?? "this state"}</div>
            {fieldMgr.length === 0 ? <div className="text-warn">None set — add a contact marked <i>field manager</i> with the state on the <Link to={`/clients/${f.project.client_id}`} className="text-accent hover:underline">client page</Link></div>
              : fieldMgr.map((c: any) => <div key={c.contact_id} className="text-text">{c.full_name} <span className="text-text-muted">· {c.role_title ?? "field manager"}{c.phone ? ` · ${c.phone}` : ""}</span></div>)}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-line p-4">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5"><Phone size={12}/> State contacts — {f.state ?? "national only (site has no state)"}</div>
          <div className="flex items-center gap-3">
            {canEdit && <button type="button" onClick={checkNumbers} disabled={checking} className="text-[12px] text-accent hover:underline inline-flex items-center gap-1 disabled:opacity-50"><ShieldCheck size={12}/> {checking ? "Checking on the web…" : "Check these numbers"}</button>}
            {canEdit && <button type="button" onClick={() => setAdding(a => !a)} className="text-[12px] text-accent hover:underline inline-flex items-center gap-1"><Plus size={12}/> Add a contact</button>}
          </div>
        </div>
        {adding && (
          <div className="grid grid-cols-1 md:grid-cols-[150px_1fr_140px_1fr_auto] gap-2 mb-3">
            <Select value={nc.category} onChange={e => setNc({ ...nc, category: e.target.value })}>{cats.map(c => <option key={c} value={c}>{CONTACT_CATEGORY_LABEL[c]}</option>)}</Select>
            <Input value={nc.name} onChange={e => setNc({ ...nc, name: e.target.value })} placeholder="Name"/>
            <Input value={nc.phone} onChange={e => setNc({ ...nc, phone: e.target.value })} placeholder="Phone" mono/>
            <Input value={nc.note} onChange={e => setNc({ ...nc, note: e.target.value })} placeholder="Note"/>
            <Button size="sm" onClick={addContact}>Add</Button>
          </div>
        )}
        <div className="divide-y divide-line/60">
          {f.stateContacts.map(c => (
            <label key={c.contact_id} className="flex items-center gap-3 py-2 text-[12px] cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-[#FF6A1A]" checked={!!verified[c.contact_id]} disabled={!canEdit} onChange={e => setVerified(v => ({ ...v, [c.contact_id]: e.target.checked }))}/>
              <span className="w-[120px] text-text-muted shrink-0">{CONTACT_CATEGORY_LABEL[c.category] ?? c.category}{c.state === "AU" ? " · national" : ""}</span>
              <span className="text-text flex-1 min-w-[160px]">{c.name}{c.note && <span className="text-text-muted"> — {c.note}</span>}</span>
              <span className="font-mono text-text">{c.phone}</span>
              {verified[c.contact_id] ? <Pill label="verified" tone="ok" small/> : <Pill label="unverified" tone="warning" small/>}
              {checks[c.contact_id] && (
                <span className="basis-full pl-7 text-[11px] flex items-center gap-2 flex-wrap">
                  {checks[c.contact_id].ok
                    ? <span className="text-ok">✓ matches what's published</span>
                    : checks[c.contact_id].phone_found
                      ? <><span className="text-warn">published as <b className="font-mono">{checks[c.contact_id].phone_found}</b></span>{canEdit && <button type="button" onClick={e => { e.preventDefault(); applyPhone(c, checks[c.contact_id].phone_found); }} className="text-accent hover:underline">use it</button>}</>
                      : <span className="text-text-muted">couldn't confirm — {checks[c.contact_id].note}</span>}
                  {checks[c.contact_id].source_url && <a href={checks[c.contact_id].source_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">source</a>}
                </span>
              )}
            </label>
          ))}
        </div>
        <div className="text-[11px] text-text-muted mt-2">Unverified numbers still show to the crew, marked "check before relying". Tick them once confirmed — it's per state, so the next job in {f.state ?? "the same state"} inherits it.</div>
      </div>
      {canEdit && <StepFoot err={err} busy={busy} onSave={save} saveLabel={f.coretel ? "Save → Sign off" : "Save"}/>}
    </div>
  );
}

// --- 6 · sign off -------------------------------------------------------------
function StepSignoff({ f, steps, canEdit, user }: { f: F; steps: ReturnType<typeof setupSteps>; canEdit: boolean; user: any }) {
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const blockers = steps.filter(s => s.n < 6 && !s.done);
  const draftSwms = (f.setup!.swms ?? []).map(a => f.swmsList.find(s => s.key === allocKey(a))).filter(s => s && !s.usable) as SwmsDoc[];
  async function sign() {
    setBusy(true); setErr(null);
    try { await saveSetup(f, { signed_off_at: new Date().toISOString(), signed_off_by: user?.user_id ?? null }, user?.user_id); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  async function unsign() {
    setBusy(true); try { await saveSetup(f, { signed_off_at: null, signed_off_by: null }, user?.user_id); } finally { setBusy(false); }
  }
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  return (
    <div>
      <StepHead n={6} title="Sign off — ready for the crew" blurb="Your name and the time go on it. Any later change to scope, SWMS or permits drops the sign-off and you sign again."/>
      <div className="rounded-lg border border-line divide-y divide-line/60">
        {steps.filter(s => s.n < 6).map(s => (
          <div key={s.key} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[11px] ${s.done ? "bg-ok" : "bg-danger"}`}>{s.done ? <Check size={12}/> : <X size={12}/>}</span>
            <span className="text-text w-[190px]">{s.label}</span>
            <span className="text-text-muted">{s.detail}</span>
          </div>
        ))}
      </div>
      {draftSwms.length > 0 && <div className="mt-3 text-[12px] text-warn flex items-center gap-1.5"><AlertTriangle size={13}/> {draftSwms.length} allocated SWMS still in draft ({draftSwms.map(s => s.number).join(", ")}) — a director approves it, or pick the standard template instead.</div>}
      {f.setup!.signed_off_at ? (
        <div className="mt-4 rounded-lg bg-ok/8 border border-ok/30 px-4 py-3 flex items-center gap-3 flex-wrap">
          <ShieldCheck size={18} className="text-ok"/>
          <div className="text-[13px] text-text flex-1">Signed off {new Date(f.setup!.signed_off_at).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} — the crew's folder is live.</div>
          {canEdit && <Button variant="ghost" size="sm" onClick={unsign} disabled={busy}>Withdraw</Button>}
        </div>
      ) : canEdit ? (
        <div className="mt-4 space-y-3">
          <label className="flex items-start gap-3 text-[13px] text-text">
            <input type="checkbox" className="mt-1 w-5 h-5 accent-[#FF6A1A]" checked={ok} onChange={e => setOk(e.target.checked)} disabled={blockers.length > 0 || draftSwms.length > 0}/>
            <span>I, <b>{name || "the manager"}</b>, have set this folder up — the scope is right, the SWMS cover it, the permits are the minimum, and the emergency details are current for this site.</span>
          </label>
          {err && <div className="text-[12px] text-danger">{err}</div>}
          <Button icon={PenLine} onClick={sign} disabled={!ok || busy || blockers.length > 0 || draftSwms.length > 0} size="lg">{busy ? "Signing…" : "Sign off — release to the crew"}</Button>
          {blockers.length > 0 && <div className="text-[12px] text-danger">Finish {blockers.map(b => `step ${b.n}`).join(", ")} first.</div>}
        </div>
      ) : <div className="mt-4 text-[12px] text-text-muted">A manager signs this off.</div>}
    </div>
  );
}
